/**
 * Form Validation Utilities
 * Client-side validation helpers
 */

/**
 * Validate email format
 */
export function validateEmail(email: string): string | null {
  if (!email) {
    return '請輸入電子郵件'
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return '請輸入有效的電子郵件'
  }

  if (email.length > 255) {
    return '電子郵件過長（最多 255 字元）'
  }

  return null
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): string | null {
  if (!password) {
    return '請輸入密碼'
  }

  if (password.length < 8) {
    return '密碼至少 8 個字元'
  }

  if (!/[A-Z]/.test(password)) {
    return '密碼須包含至少一個大寫字母'
  }

  if (!/[a-z]/.test(password)) {
    return '密碼須包含至少一個小寫字母'
  }

  if (!/[0-9]/.test(password)) {
    return '密碼須包含至少一個數字'
  }

  return null
}

/**
 * Validate required field
 */
export function validateRequired(value: string, fieldName: string = '此欄位'): string | null {
  if (!value || value.trim() === '') {
    return `${fieldName}為必填`
  }

  return null
}

/**
 * Validate string length
 */
export function validateLength(
  value: string,
  min?: number,
  max?: number,
  fieldName: string = '此欄位'
): string | null {
  if (min !== undefined && value.length < min) {
    return `${fieldName}至少 ${min} 個字元`
  }

  if (max !== undefined && value.length > max) {
    return `${fieldName}最多 ${max} 個字元`
  }

  return null
}

/**
 * Validate number range
 */
export function validateNumberRange(
  value: number,
  min?: number,
  max?: number,
  fieldName: string = '此值'
): string | null {
  if (min !== undefined && value < min) {
    return `${fieldName}不可小於 ${min}`
  }

  if (max !== undefined && value > max) {
    return `${fieldName}不可大於 ${max}`
  }

  return null
}

/**
 * Validate bot name
 */
export function validateBotName(name: string): string | null {
  const requiredError = validateRequired(name, 'Bot 名稱')
  if (requiredError) return requiredError

  return validateLength(name, 1, 100, 'Bot 名稱')
}

/**
 * Validate API key label
 */
export function validateApiKeyLabel(label: string): string | null {
  if (!label) return null // Optional field

  return validateLength(label, 1, 50, 'API Key 標籤')
}

/**
 * Validate daily rate
 */
export function validateDailyRate(rate: number): string | null {
  return validateNumberRange(rate, 0, 1, '日利率')
}

/**
 * Validate spread lend count
 */
export function validateSpreadLend(count: number): string | null {
  return validateNumberRange(count, 1, 100, '掛單數量')
}

/**
 * Validate period days
 */
export function validatePeriod(days: number): string | null {
  return validateNumberRange(days, 2, 120, '期限天數')
}

/**
 * Validate form with multiple fields
 */
export function validateForm(
  values: Record<string, unknown>,
  validators: Record<string, (value: unknown) => string | null>
): Record<string, string> {
  const errors: Record<string, string> = {}

  for (const [field, validator] of Object.entries(validators)) {
    const error = validator(values[field])
    if (error) {
      errors[field] = error
    }
  }

  return errors
}

/**
 * Check if form has errors
 */
export function hasErrors(errors: Record<string, string>): boolean {
  return Object.keys(errors).length > 0
}
